import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter 
} from 'firebase/firestore';
import { firestore } from '../firebase/config';
import { BusinessService } from './BusinessService';
import ChatService from './ChatService';
import { auth } from '../firebase/config';

/**
 * JobService - Centralized service for all job-related operations
 * This service handles job data fetching, processing, and formatting
 * with consistent salary validation and business data integration
 */
export class JobService {
  /**
   * Fetch jobs with comprehensive filtering and pagination
   * @param {Object} options - Fetch options
   * @param {string} options.searchQuery - Search term for jobs
   * @param {string} options.locationQuery - Location filter
   * @param {number} options.minSalary - Minimum salary filter
   * @param {number} options.maxSalary - Maximum salary filter
   * @param {string} options.jobType - Job type filter
   * @param {DocumentSnapshot} options.lastDoc - For pagination
   * @param {number} options.limit - Number of jobs to fetch
   * @returns {Object} { jobs: Array, lastDoc: DocumentSnapshot, hasMore: boolean }
   */
  static async fetchJobs(options = {}) {
    const {
      searchQuery,
      locationQuery,
      minSalary,
      maxSalary,
      jobType,
      lastDoc,
      limit: fetchLimit = 30
    } = options;

    try {
      console.log('🔍 JobService: Fetching jobs with options:', options);
      
      const jobsRef = collection(firestore, 'jobs');
      
      // Build base query - start with ordering for pagination
      let jobsQuery = query(
        jobsRef,
        orderBy('createdAt', 'desc'),
        limit(fetchLimit)
      );
      
      // Add pagination if lastDoc provided
      if (lastDoc) {
        jobsQuery = query(
          jobsRef,
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(fetchLimit)
        );
      }
      
      const jobsSnapshot = await getDocs(jobsQuery);
      console.log(`📊 JobService: Retrieved ${jobsSnapshot.size} jobs from database`);
      
      if (jobsSnapshot.empty) {
        return { jobs: [], lastDoc: null, hasMore: false };
      }
      
      // Get all unique business IDs
      const businessIds = new Set();
      jobsSnapshot.docs.forEach(doc => {
        const jobData = doc.data();
        if (jobData.businessId) {
          businessIds.add(jobData.businessId);
        }
      });
      
      // Fetch business data efficiently
      const businessDataMap = await BusinessService.fetchBusinessesBatch(Array.from(businessIds));
      
      // Process all jobs
      const processedJobs = jobsSnapshot.docs.map(doc => 
        this.processJobData(doc, businessDataMap)
      );
      
      // Apply client-side filters for more flexible searching
      let filteredJobs = this.applyClientFilters(processedJobs, {
        searchQuery,
        locationQuery,
        minSalary,
        maxSalary,
        jobType
      });
      
      const newLastDoc = jobsSnapshot.docs[jobsSnapshot.docs.length - 1];
      const hasMore = jobsSnapshot.size >= fetchLimit;
      
      console.log(`✅ JobService: Returning ${filteredJobs.length} filtered jobs`);
      
      return {
        jobs: filteredJobs,
        lastDoc: newLastDoc,
        hasMore
      };
      
    } catch (error) {
      console.error('❌ JobService: Error fetching jobs:', error);
      throw new Error(`Failed to fetch jobs: ${error.message}`);
    }
  }

  /**
   * Fetch jobs for specific business IDs (used by map components)
   * @param {Array<string>} businessIds - Array of business IDs
   * @returns {Object} Map of businessId -> jobs array
   */
  static async fetchJobsForBusinesses(businessIds) {
    if (!businessIds || businessIds.length === 0) {
      return {};
    }

    try {
      console.log(`🔍 JobService: Fetching jobs for ${businessIds.length} businesses`);
      console.log(`🔍 Business IDs:`, businessIds);
      
      const jobsByBusiness = {};
      const batchSize = 10; // Firestore 'in' query limit
      let totalJobsFound = 0;
      
      // Process business IDs in batches
      for (let i = 0; i < businessIds.length; i += batchSize) {
        const batchIds = businessIds.slice(i, i + batchSize);
        console.log(`🔍 Processing batch ${Math.floor(i/batchSize) + 1} with IDs:`, batchIds);
        
        const jobsRef = collection(firestore, 'jobs');
        
        // Use the same approach as JobListPage - simple query first
        const jobsQuery = query(
          jobsRef, 
          where('businessId', 'in', batchIds)
          // Remove the problematic isActive filter - we'll filter client-side
        );
        
        console.log(`🔍 Executing Firestore query for batch ${Math.floor(i/batchSize) + 1}...`);
        const jobSnapshot = await getDocs(jobsQuery);
        
        console.log(`📄 JobService: Batch ${Math.floor(i/batchSize) + 1}: Found ${jobSnapshot.docs.length} total jobs`);
        
        jobSnapshot.docs.forEach(doc => {
          const job = { id: doc.id, ...doc.data() };
          console.log(`📋 Processing job: ${job.title} for business ${job.businessId} - isActive: ${job.isActive}`);
          
          // Client-side filtering for active jobs (same as JobListPage approach)
          const isActive = job.isActive !== false; // This matches the JobListPage logic
          
          if (job.businessId && isActive) {
            if (!jobsByBusiness[job.businessId]) {
              jobsByBusiness[job.businessId] = [];
            }
            // Process and validate job data
            const processedJob = this.processJobDataMinimal(job);
            jobsByBusiness[job.businessId].push(processedJob);
            totalJobsFound++;
          } else {
            if (!job.businessId) {
              console.warn(`⚠️ Job ${job.id} has no businessId!`);
            }
            if (!isActive) {
              console.log(`ℹ️ Job ${job.id} (${job.title}) is inactive, skipping`);
            }
          }
        });
      }
      
      // Sort jobs within each business by hourly rate (highest first)
      Object.keys(jobsByBusiness).forEach(businessId => {
        jobsByBusiness[businessId].sort((a, b) => (b.hourlyRate || 0) - (a.hourlyRate || 0));
      });
      
      console.log(`📊 JobService: Total jobs found: ${totalJobsFound} for ${Object.keys(jobsByBusiness).length} businesses`);
      
      return jobsByBusiness;
      
    } catch (error) {
      console.error('❌ JobService: Error fetching jobs for businesses:', error);
      throw new Error(`Failed to fetch jobs for businesses: ${error.message}`);
    }
  }

  /**
   * Fetch a single job by ID with business data
   * @param {string} jobId - Job ID
   * @returns {Object|null} Processed job object or null if not found
   */
  static async fetchJobById(jobId) {
    try {
      console.log(`🔍 JobService: Fetching job ${jobId}`);
      
      const jobDoc = await getDoc(doc(firestore, 'jobs', jobId));
      
      if (!jobDoc.exists()) {
        console.log('❌ JobService: Job not found');
        return null;
      }
      
      const jobData = jobDoc.data();
      
      // Get business data if available
      let businessData = {};
      if (jobData.businessId) {
        businessData = await BusinessService.fetchBusinessById(jobData.businessId);
      }
      
      const processedJob = this.processJobData(jobDoc, { [jobData.businessId]: businessData });
      
      console.log('✅ JobService: Job fetched successfully');
      return processedJob;
      
    } catch (error) {
      console.error('❌ JobService: Error fetching job:', error);
      throw new Error(`Failed to fetch job: ${error.message}`);
    }
  }

  /**
   * Fetch similar jobs based on job type or location
   * @param {Object} jobData - Current job data
   * @param {number} limit - Number of similar jobs to fetch
   * @returns {Array} Array of similar jobs
   */
  static async fetchSimilarJobs(jobData, limit = 4) {
    try {
      console.log('🔍 JobService: Fetching similar jobs');
      
      let similarJobsQuery;
      
      if (jobData.jobType) {
        similarJobsQuery = query(
          collection(firestore, 'jobs'),
          where('jobType', '==', jobData.jobType),
          where('isActive', '!=', false),
          orderBy('isActive'),
          orderBy('createdAt', 'desc'),
          limit(limit + 1) // +1 to account for excluding current job
        );
      } else if (jobData.address?.city) {
        similarJobsQuery = query(
          collection(firestore, 'jobs'),
          where('address.city', '==', jobData.address.city),
          where('isActive', '!=', false),
          orderBy('isActive'),
          orderBy('createdAt', 'desc'),
          limit(limit + 1)
        );
      }
      
      if (!similarJobsQuery) {
        return [];
      }
      
      const similarJobsSnapshot = await getDocs(similarJobsQuery);
      
      // Filter out current job and process data
      const similarJobs = similarJobsSnapshot.docs
        .filter(doc => doc.id !== jobData.id)
        .slice(0, limit)
        .map(doc => this.processJobDataMinimal({ id: doc.id, ...doc.data() }));
      
      console.log(`✅ JobService: Found ${similarJobs.length} similar jobs`);
      return similarJobs;
      
    } catch (error) {
      console.error('❌ JobService: Error fetching similar jobs:', error);
      return [];
    }
  }

  /**
   * Process job document data with business information
   * @param {DocumentSnapshot} doc - Firestore document
   * @param {Object} businessDataMap - Map of business ID to business data
   * @returns {Object} Processed job object
   */
  static processJobData(doc, businessDataMap = {}) {
    const jobData = doc.data();
    const jobId = doc.id;
    const businessData = businessDataMap[jobData.businessId] || {};
    
    // Calculate posted days
    const postedDays = this.calculatePostedDays(jobData.createdAt);
    
    // Get job location with fallbacks
    const location = this.getJobLocation(jobData, businessData);
    
    // Format and validate salary
    const { formattedSalary, hourlyRate } = this.formatSalary(jobData);
    
    // Get business image path
    const businessImagePath = businessData.businessImage?.path || null;
    
    // Build comprehensive job object
    return {
      id: jobId,
      title: jobData.title || 'Untitled Position',
      businessName: businessData.name || 'Unknown Business',
      location,
      salary: formattedSalary,
      hourlyRate, // Validated numeric value
      postedDays,
      description: jobData.description || 'No description available',
      address: this.formatAddress(jobData, businessData),
      pay: formattedSalary,
      jobType: jobData.jobType || 'Not specified',
      experienceLevel: jobData.experienceLevel || 'Not specified',
      businessId: jobData.businessId,
      businessLogo: businessImagePath || businessData.logoUrl || null,
      businessImagePath,
      immediateStart: jobData.isImmediateStart || false,
      applicationUrl: jobData.applicationUrl || null,
      isActive: jobData.isActive !== false,
      workSchedule: jobData.workSchedule || 'Not specified',
      benefits: jobData.benefits || 'Not specified',
      qualifications: jobData.qualifications || 'Not specified',
      responsibilities: jobData.responsibilities || 'Not specified',
      // Search optimization
      searchTerms: this.buildSearchTerms(jobData, businessData, location)
    };
  }

  /**
   * Process job data without business lookup (for performance)
   * @param {Object} jobData - Raw job data with ID
   * @returns {Object} Minimally processed job object
   */
  static processJobDataMinimal(jobData) {
    const { formattedSalary, hourlyRate } = this.formatSalary(jobData);
    
    return {
      id: jobData.id,
      title: jobData.title || 'Untitled Position',
      description: jobData.description || 'No description available',
      salary: formattedSalary,
      hourlyRate, // Validated numeric value
      jobType: jobData.jobType || 'Not specified',
      businessId: jobData.businessId,
      applicationUrl: jobData.applicationUrl || null,
      isActive: jobData.isActive !== false,
      immediateStart: jobData.isImmediateStart || false,
      postedDays: this.calculatePostedDays(jobData.createdAt)
    };
  }

  /**
   * Format and validate salary data with proper error handling
   * @param {Object} jobData - Raw job data
   * @returns {Object} { formattedSalary: string, hourlyRate: number }
   */
  static formatSalary(jobData) {
    let formattedSalary = 'Salary not specified';
    let hourlyRate = 0;
    
    if (jobData.hourlyRate) {
      const rawRate = typeof jobData.hourlyRate === 'number' 
        ? jobData.hourlyRate 
        : parseFloat(jobData.hourlyRate);
      
      if (!isNaN(rawRate) && rawRate > 0) {
        // Validate and fix unrealistic hourly rates
        if (rawRate > 500) {
          // Likely an annual salary mistakenly entered as hourly
          // Convert to reasonable hourly rate (assuming 2080 work hours/year)
          hourlyRate = Math.round((rawRate / 2080) * 100) / 100;
          formattedSalary = `$${hourlyRate.toFixed(2)}/hr (est.)`;
          console.warn(`⚠️ Corrected unrealistic hourly rate: $${rawRate} -> $${hourlyRate}/hr`);
        } else if (rawRate >= 100) {
          // High but reasonable hourly rate
          hourlyRate = rawRate;
          formattedSalary = `$${rawRate.toFixed(2)}/hr`;
        } else {
          // Normal hourly rate
          hourlyRate = rawRate;
          formattedSalary = `$${rawRate.toFixed(2)}/hr`;
        }
      }
    } else if (jobData.salary) {
      formattedSalary = jobData.salary;
      // Try to extract hourly rate from salary string if possible
      const salaryMatch = jobData.salary.match(/\$?(\d+(?:\.\d{2})?)/);
      if (salaryMatch) {
        const extractedRate = parseFloat(salaryMatch[1]);
        if (!isNaN(extractedRate) && extractedRate <= 500) {
          hourlyRate = extractedRate;
        }
      }
    }
    
    return { formattedSalary, hourlyRate };
  }

  /**
   * Calculate days since job was posted
   * @param {*} createdAt - Firestore timestamp
   * @returns {number} Days since posted
   */
  static calculatePostedDays(createdAt) {
    try {
      const postedDate = createdAt?.__time__ 
        ? new Date(createdAt.__time__) 
        : createdAt?.toDate?.() || new Date();
        
      const currentDate = new Date();
      const diffTime = currentDate - postedDate;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (err) {
      console.warn('⚠️ Error calculating posted days:', err);
      return 0;
    }
  }

  /**
   * Get job location with business fallbacks
   * @param {Object} jobData - Job data
   * @param {Object} businessData - Business data
   * @returns {string} Location string
   */
  static getJobLocation(jobData, businessData) {
    return jobData.address?.city || 
           businessData.address?.city || 
           businessData.location?.city || 
           'Unknown Location';
  }

  /**
   * Format complete address string
   * @param {Object} jobData - Job data
   * @param {Object} businessData - Business data
   * @returns {string} Formatted address
   */
  static formatAddress(jobData, businessData) {
    if (jobData.address?.street) {
      return `${jobData.address.street}, ${jobData.address.city || ''}, ${jobData.address.state || ''} ${jobData.address.zipcode || ''}`.trim();
    }
    
    if (businessData.address?.street) {
      return `${businessData.address.street}, ${businessData.address.city || ''}, ${businessData.address.state || ''} ${businessData.address.zipcode || ''}`.trim();
    }
    
    return 'Address not specified';
  }

  /**
   * Build search terms for the job
   * @param {Object} jobData - Job data
   * @param {Object} businessData - Business data
   * @param {string} location - Job location
   * @returns {string} Search terms string
   */
  static buildSearchTerms(jobData, businessData, location) {
    return `${jobData.title || ''} ${businessData.name || ''} ${jobData.description || ''} ${jobData.jobType || ''} ${location || ''}`.toLowerCase();
  }

  /**
   * Apply client-side filters to jobs array
   * @param {Array} jobs - Array of job objects
   * @param {Object} filters - Filter options
   * @returns {Array} Filtered jobs array
   */
  static applyClientFilters(jobs, filters = {}) {
    const { searchQuery, locationQuery, minSalary, maxSalary, jobType } = filters;
    
    let filtered = [...jobs];
    
    // Search query filter
    if (searchQuery && searchQuery.trim()) {
      const searchTerms = searchQuery.toLowerCase().trim().split(' ').filter(term => term.length > 0);
      
      filtered = filtered.filter(job => {
        const titleMatch = job.title && searchTerms.some(term => 
          job.title.toLowerCase().includes(term));
        
        const descriptionMatch = job.description && searchTerms.some(term => 
          job.description.toLowerCase().includes(term));
          
        const businessNameMatch = job.businessName && searchTerms.some(term => 
          job.businessName.toLowerCase().includes(term));
          
        const jobTypeMatch = job.jobType && searchTerms.some(term => 
          job.jobType.toLowerCase().includes(term));
        
        return titleMatch || descriptionMatch || businessNameMatch || jobTypeMatch;
      });
    }
    
    // Location filter
    if (locationQuery && locationQuery.trim()) {
      const locationTerms = locationQuery.toLowerCase().trim().split(' ').filter(term => term.length > 0);
      
      filtered = filtered.filter(job => {
        const locationMatch = job.location && locationTerms.some(term => 
          job.location.toLowerCase().includes(term));
          
        const addressMatch = job.address && locationTerms.some(term => 
          job.address.toLowerCase().includes(term));
        
        return locationMatch || addressMatch;
      });
    }
    
    // Salary range filter
    if (minSalary > 0) {
      filtered = filtered.filter(job => (job.hourlyRate || 0) >= minSalary);
    }
    
    if (maxSalary > 0) {
      filtered = filtered.filter(job => (job.hourlyRate || 0) <= maxSalary);
    }
    
    // Job type filter
    if (jobType && jobType !== 'all') {
      filtered = filtered.filter(job => 
        job.jobType.toLowerCase() === jobType.toLowerCase()
      );
    }
    
    return filtered;
  }

  async searchUsers(searchTerm) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      
      // Use ChatService's searchUsers method which handles both user types
      const chatService = new ChatService();
      return await chatService.searchUsers(searchTerm, currentUser.uid);
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }
}

export default JobService; 